import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card } from '@codes/ui-kit/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@codes/ui-kit/components/ui/dropdown-menu';
import { Image } from '@codes/ui-kit/components/ui/image';
import { ProjectSettingDialog } from '@web/app/features/home/components';
import { useProjectService, useProjectSettingDialog, useWorkspaceService } from '@web/app/features/home/hooks';
import { useCreateProjectDialog, useMyRole, useSelectedStore } from '@web/app/shared';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { EllipsisVertical, Plus, RefreshCw } from 'lucide-react';

import { ListEmptyImage } from '@codes/assets';
import { Button, Skeleton } from '@codes/ui-kit';
import { formatDate } from '@codes/utils';

export const ProjectListPage = () => {
    const { workspaceFromServerQueryData, workspaceFromServer } = useWorkspaceService();
    const { myProjectsQueryResult } = useProjectService();
    const { setProject, workspace } = useSelectedStore();
    const navigate = useNavigate();

    const { setProjectDialog } = useCreateProjectDialog({});
    const { open } = useProjectSettingDialog();
    const { t } = useTranslation();

    const myRole = useMyRole();
    const isAccessable = myRole <= 1;

    if (workspaceFromServerQueryData.isFetching && !workspaceFromServer) {
        return (
            <PageContainer className="flex flex-col h-full animate-enter">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] max-xs:grid-cols-1 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-36 rounded-lg" />
                    ))}
                </div>
            </PageContainer>
        );
    }

    if (!workspaceFromServer) {
        return null;
    }

    const isRequested = workspaceFromServer.state === 'requested';

    if (isRequested) {
        return (
            <PageContainer className="flex flex-col gap-y-16 h-full animate-enter">
                <div className="flex items-center justify-end">
                    <Button
                        onClick={() => workspaceFromServerQueryData.refetch()}
                        size="sm"
                        className="gap-1.5"
                        disabled={workspaceFromServerQueryData.isRefetching}
                    >
                        <RefreshCw
                            size={14}
                            className={workspaceFromServerQueryData.isRefetching ? 'animate-spin' : ''}
                        />
                        {t('box:editorPage.load.refresh')}
                    </Button>
                </div>
                <div className="flex-1 flex flex-col items-center justify-center -translate-y-12">
                    <div className="flex gap-2 mb-6">
                        <div className="w-2.5 h-2.5 bg-point rounded-full animate-bounce [animation-delay:0ms]" />
                        <div className="w-2.5 h-2.5 bg-point rounded-full animate-bounce [animation-delay:150ms]" />
                        <div className="w-2.5 h-2.5 bg-point rounded-full animate-bounce [animation-delay:300ms]" />
                    </div>
                    <div className="text-xl font-semibold font-display mb-2">{t('workspace.preparingMessageText')}</div>
                    <div className="text-sm text-text-500 text-center max-w-md leading-relaxed">
                        {t('workspace.preparingMessageDesc')}
                    </div>
                </div>
            </PageContainer>
        );
    }

    const isLoading = myProjectsQueryResult.isFetching || myProjectsQueryResult.isRefetching;

    if (isLoading) {
        return (
            <PageContainer className="flex flex-col animate-enter">
                <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] max-xs:grid-cols-1 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-36 rounded-lg" />
                    ))}
                </div>
            </PageContainer>
        );
    }

    if (myProjectsQueryResult.error) {
        return (
            <PageContainer className="flex flex-col">
                <div className="flex flex-col items-center justify-center gap-4 py-20">
                    <div className="w-14 h-14 rounded-full bg-error/10 flex items-center justify-center">
                        <RefreshCw className="w-6 h-6 text-error" />
                    </div>
                    <div className="text-error text-sm font-medium">{t('project.error.fetchList')}</div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => myProjectsQueryResult.refetch()}
                        disabled={myProjectsQueryResult.isRefetching}
                    >
                        {t('project.error.retry')}
                    </Button>
                </div>
            </PageContainer>
        );
    }

    const isEmpty = myProjectsQueryResult.data?.data.length === 0;

    if (isEmpty) {
        return (
            <PageContainer className="flex-1 flex flex-col justify-center animate-enter">
                <h1 className="text-lg font-semibold font-display">Projects</h1>
                <div className="flex-1 flex flex-col justify-center items-center -translate-y-16">
                    <Image className="max-w-[120px] opacity-70" src={ListEmptyImage} />
                    <div className="text-xl font-semibold font-display mb-2 mt-5">{t('project.emptyState.title')}</div>
                    <div className="text-sm text-text-500 mb-5 text-center">{t('project.emptyState.description')}</div>
                    <Button size="sm" className="gap-1.5" onClick={() => setProjectDialog(true)}>
                        <Plus size={14} /> {t('project.addProject')}
                    </Button>
                </div>
            </PageContainer>
        );
    }

    return (
        <PageContainer className="flex flex-col animate-enter">
            <div className="flex items-center justify-between mb-8">
                <h1 className="text-lg font-semibold font-display">Projects</h1>
                <Button size="sm" className="gap-1.5" onClick={() => setProjectDialog(true)}>
                    <Plus size={14} /> {t('project.addProject')}
                </Button>
            </div>

            <div className="grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] max-xs:grid-cols-1 gap-4 animate-stagger">
                {myProjectsQueryResult.data?.data.map(project => (
                    <Card
                        onClick={() => {
                            setProject(project);
                            if (workspace?.id) {
                                navigate(`/w/${workspace.id}/p/${project.id}`);
                            }
                        }}
                        className="cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200"
                        key={project.id}
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-text-500 text-xs">{formatDate(project.createdAt, 'YYYY. MM. DD')}</div>
                            {isAccessable && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" className="p-1 group w-7 h-7">
                                            <EllipsisVertical className="w-4 h-4 text-text-400 group-hover:text-text transition-colors duration-150" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="center" className="w-24 p-1">
                                        <DropdownMenuItem
                                            onClick={e => {
                                                e.stopPropagation();
                                                open(project.id as string);
                                            }}
                                        >
                                            Setting
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>

                        <div className="border-b border-border my-3" />
                        <div className="truncate text-sm font-medium">{project.name}</div>
                    </Card>
                ))}
            </div>
            <ProjectSettingDialog />
        </PageContainer>
    );
};
